import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { drawingsApi, type Drawing } from '@/api/endpoints';
import { Alert, Button, Card, Field, Input } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';

/**
 * 图纸 / 进度表的上传表单。管理端两个页面共用。
 *
 * 搬到这里是因为进度表独立成了自己的一页（CR-028）—— 在那之前它是
 * `DrawingsPage` 里的一个局部组件，而进度表那一块也挤在同一页上。
 * 两页共用一份：上传逻辑（直传签名、一张一张传、失败了只摘掉成功的那几张）
 * 只该有一份实现。
 */

/** 只收 PDF 和图片。DWG 要服务端转换，成本高，让上级自己导出 PDF。 */
const ACCEPTED = 'application/pdf,image/jpeg,image/png';

/**
 * 进度表收图片和 PDF（CR-026）。
 *
 * CR-023 定的是「只收图片」，理由是它给工人扫一眼、不是要逐页翻的图纸。
 * 但客户手上的进度表本来就是 PDF —— 只收图片等于逼他先截图再传，
 * 截出来的甘特图在手机上根本看不清字。查看器两种都认，这个限制没有收益。
 */
const ACCEPTED_SCHEDULE = 'application/pdf,image/jpeg,image/png';

/** 选中待传的一张图纸。图纸名默认取文件名，可以逐张改 */
interface Picked {
  /** 列表 key。文件名可能重名，用它稳定标识 */
  key: string;
  file: File;
  title: string;
}

const stripExtension = (name: string) => name.replace(/\.[^.]+$/, '');

/**
 * 上传表单。两种模式（CR-023）：
 * - `DRAWING`：多选、PDF 和图片、可填分类；
 * - `SCHEDULE`：单张图片、不填分类，带 `supersedesId` 顶掉当前那张。
 */
export function UploadForm({
  siteId,
  onDone,
  mode = 'DRAWING',
  supersedesId,
  existing = [],
  bare = false,
}: {
  siteId: number;
  onDone: () => void;
  mode?: 'DRAWING' | 'SCHEDULE';
  supersedesId?: string;
  /** 这个工地现有的图纸，用来认出重名（CR-027） */
  existing?: Drawing[];
  /** 放在面板里时不再套卡片（CR-082 上传图纸在右侧面板里） */
  bare?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();

  const isSchedule = mode === 'SCHEDULE';
  const accept = isSchedule ? ACCEPTED_SCHEDULE : ACCEPTED;
  const acceptedTypes = new Set(accept.split(','));

  const [items, setItems] = useState<Picked[]>([]);
  const [category, setCategory] = useState('');
  const [dragging, setDragging] = useState(false);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<Array<{ name: string; reason: string }>>([]);
  const [progress, setProgress] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * 已有的同名图纸，按标题查。
   *
   * 比的是**去掉首尾空格之后**的标题：从文件名推出来的标题很容易带上空格，
   * 「建筑总平面图 」和「建筑总平面图」在客户眼里是同一张。
   */
  const existingByTitle = new Map(existing.map((d) => [d.title.trim(), d]));

  /**
   * 收下一批文件。
   *
   * **拖进来的文件不受 `accept` 约束** —— `accept` 只管系统文件选择框里的过滤，
   * 拖放能把任何东西丢进来。所以这里必须自己再筛一遍，
   * 否则一个 .dwg 会一路传到服务端才被拒。
   */
  function addFiles(incoming: FileList | File[]) {
    const accepted: Picked[] = [];
    const rejected: string[] = [];

    for (const file of Array.from(incoming)) {
      if (!acceptedTypes.has(file.type)) {
        rejected.push(file.name);
        continue;
      }
      accepted.push({
        key: `${file.name}:${file.size}:${file.lastModified}`,
        file,
        // 文件名通常就是图纸名，省得再手打一遍
        title: stripExtension(file.name).slice(0, 200),
      });
    }

    setSkipped(rejected);
    // 进度表只有一张，新选的直接顶掉上一张，不往列表里追加
    if (isSchedule) {
      setItems(accepted.slice(-1));
      return;
    }
    setItems((prev) => {
      // 同一个文件拖两次只留一份 —— 不然会传两遍，生成两条图纸记录
      const seen = new Set(prev.map((p) => p.key));
      return [...prev, ...accepted.filter((p) => !seen.has(p.key))];
    });
  }

  const upload = useMutation({
    mutationFn: async () => {
      const failed: Array<{ name: string; reason: string }> = [];
      const uploaded = new Set<string>();

      // 一张一张传，不并发：工地网络本来就不稳，十几个并发 PUT 互相抢带宽，
      // 结果是整批都慢、且更容易超时。顺序传还能把进度说清楚是第几张。
      for (const [index, item] of items.entries()) {
        setProgress(
          t('drawings.uploadingNth', {
            current: index + 1,
            total: items.length,
            name: item.file.name,
          }),
        );

        try {
          // 图纸走和照片一样的直传：先换签名 URL，再 PUT 上去，
          // 最后只把元数据交给 API。几十兆的图纸不该穿过 Node 进程。
          const presign = await api.post<{ uploadUrl: string; fileKey: string }>(
            '/files/presign',
            {
              siteId,
              kind: 'PDF',
              mimeType: item.file.type,
              sizeBytes: item.file.size,
            },
          );

          const response = await fetch(presign.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': item.file.type },
            body: item.file,
          });
          if (!response.ok) throw new Error(`上传失败 HTTP ${response.status}`);

          await drawingsApi.create(siteId, {
            title: item.title.trim() || stripExtension(item.file.name),
            category: isSchedule ? undefined : category.trim() || undefined,
            fileKey: presign.fileKey,
            mimeType: item.file.type,
            sizeBytes: item.file.size,
            ...(isSchedule ? { kind: 'SCHEDULE' as const } : {}),
            ...(supersedesId ? { supersedesId } : {}),
          });
          uploaded.add(item.key);
        } catch (err) {
          // 一张失败不能把整批拖下水：前面传成功的必须留住，
          // 失败的留在列表里让人直接重试，而不是整批从头再来一遍。
          //
          // **原因要说出来**（CR-023）：改之前只列文件名，
          // FILE_TOO_LARGE 带的 limitMb 被整个吞掉 —— 客户实测 90MB 的工程图
          // 传失败，界面上只有一个文件名，完全看不出是超了上限还是网断了。
          failed.push({ name: item.file.name, reason: toMessage(err) });
        }
      }

      return { uploaded, failed };
    },
    onSuccess: ({ uploaded, failed }) => {
      void queryClient.invalidateQueries({ queryKey: ['drawings'] });
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
      setProgress(null);

      if (failed.length === 0) {
        onDone();
        return;
      }

      // 只把传成功的从列表里摘掉，剩下的就是待重试的那几张
      setItems((prev) => prev.filter((p) => !uploaded.has(p.key)));
      setFailures(failed);
    },
    onError: (err) => {
      setProgress(null);
      setError(toMessage(err));
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFailures([]);
    if (items.length === 0) return;
    upload.mutate();
  }

  const duplicates = items
    .map((item) => item.title.trim())
    .filter((title) => existingByTitle.has(title));

  const form = (
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert>{error}</Alert>}

        {failures.length > 0 && (
          <Alert>
            <p>{t('drawings.partialFailed', { count: failures.length })}</p>
            <ul className="mt-1.5 flex flex-col gap-0.5">
              {failures.map((f) => (
                <li key={f.name} className="text-body">
                  <span className="font-medium">{f.name}</span>：{f.reason}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {skipped.length > 0 && (
          <Alert tone="info">
            {t('drawings.skippedFiles', { count: skipped.length, names: skipped.join('、') })}
          </Alert>
        )}

        {/*
          标签和提示跟着模式走：进度表那一页说「图纸文件」、还讲一句
          「CAD 请先导出成 PDF」，是拿图纸的文案糊在进度表上（CR-028）。
          进度表的格式说明在下面拖放区里已经有一句，不用再重复。
        */}
        <Field
          label={isSchedule ? t('schedule.file') : t('drawings.file')}
          htmlFor="dw-file"
          hint={isSchedule ? undefined : t('drawings.fileHint')}
          required
        >
          {/*
            拖放区自己就是按钮：点它等于点文件选择框。
            用 <button> 而不是 <div onClick>，是为了键盘也能打开选择框 ——
            管理端是坐在电脑前用的，Tab + 回车是正经操作方式。
          */}
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => {
              // 不 preventDefault 的话浏览器会接管，drop 根本不触发
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              // 拖过子元素时 dragleave 也会冒上来，不判断一下会闪
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
            }}
            className={cn(
              'flex min-h-row w-full flex-col items-center justify-center gap-1 rounded-md',
              'border-2 border-dashed px-4 py-8 text-body transition-colors',
              dragging
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-rule bg-surface-2 text-ink-3 hover:border-brand-200 hover:text-ink-2',
            )}
          >
            <span className="font-medium">
              {dragging
                ? t('drawings.dropActive')
                : isSchedule
                  ? t('schedule.dropHere')
                  : t('drawings.dropHere')}
            </span>
            <span className="text-caption">
              {isSchedule ? t('schedule.fileHint') : t('drawings.dropMultiHint')}
            </span>
          </button>

          <input
            ref={fileInput}
            id="dw-file"
            type="file"
            accept={accept}
            multiple={!isSchedule}
            hidden
            onChange={(e) => {
              if (e.target.files) addFiles(e.target.files);
              // 清空，否则连续两次选同一个文件不会触发 change
              e.target.value = '';
            }}
          />
        </Field>

        {items.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-body font-medium text-ink-2">
              {t('drawings.pickedCount', { count: items.length })}
            </p>
            <ul className="flex flex-col gap-2">
              {items.map((item, index) => (
                <li key={item.key} className="flex items-center gap-2">
                  <Input
                    value={item.title}
                    aria-label={t('drawings.title')}
                    maxLength={200}
                    required
                    onChange={(e) => {
                      const next = e.target.value;
                      setItems((prev) =>
                        prev.map((p, i) => (i === index ? { ...p, title: next } : p)),
                      );
                    }}
                  />
                  <span className="w-24 shrink-0 truncate text-caption text-ink-3" title={item.file.name}>
                    {item.file.name}
                  </span>
                  {existingByTitle.has(item.title.trim()) && (
                    <span className="shrink-0 text-caption font-medium text-safety-600">
                      {t('drawings.duplicateTitle')}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    aria-label={t('drawings.removeFile')}
                    onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                  >
                    {t('drawings.removeFile')}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isSchedule && (
          <Field label={t('drawings.category')} htmlFor="dw-cat" hint={t('drawings.categoryAllHint')}>
            <Input
              id="dw-cat"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              maxLength={64}
              placeholder={t('drawings.categoryPlaceholder')}
            />
          </Field>
        )}

        {/*
          重名的整体提醒。放在提交按钮上方而不是拦在提交里 ——
          同名未必是重复（不同分区可能真有两张「一层平面图」），
          判断交给人，系统只负责让他看见。
        */}
        {duplicates.length > 0 && (
          <Alert tone="info">
            {t('drawings.duplicateHint', {
              count: duplicates.length,
              names: duplicates.join('、'),
            })}
          </Alert>
        )}

        {progress && <p className="text-body text-ink-3">{progress}</p>}

        <div className="flex gap-3">
          <Button type="submit" loading={upload.isPending} disabled={items.length === 0}>
            {isSchedule ? t('app.save') : t('drawings.uploadCount', { count: items.length })}
          </Button>
          <Button type="button" variant="secondary" onClick={onDone}>
            {t('app.cancel')}
          </Button>
        </div>
      </form>
  );

  // 进度表那一块本身已经在一张 Card 里了，再套一层会出现双重边框
  return isSchedule || bare ? form : <Card className="mb-6 p-5">{form}</Card>;
}
